'use client';

import { useAuth } from '@/lib/integrations/supabase';
import { Button } from '@/components/shared/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shared/ui/tooltip';
import { ArrowLeft, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/integrations/language';

interface AdminContextResetProps {
  className?: string;
  variant?: 'icon' | 'button';
}

export function AdminContextReset({ className, variant = 'icon' }: AdminContextResetProps) {
  const { isAdmin, adminSelectedBusinessId, clearAdminBusinessContext } = useAuth();
  const { isRTL } = useLanguage();

  // Only show for admins with a selected business
  if (!isAdmin || !adminSelectedBusinessId) {
    return null;
  }

  if (variant === 'button') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={clearAdminBusinessContext}
        className={cn(
          "border-border text-muted-foreground",
          "hover:bg-muted hover:text-foreground",
          className
        )}
      >
        <ArrowLeft className={cn("h-4 w-4", isRTL ? "ml-2 rotate-180" : "mr-2")} />
        Back to Businesses
      </Button>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={clearAdminBusinessContext}
            className={cn(
              "h-8 w-8 text-muted-foreground",
              "hover:bg-muted hover:text-foreground",
              className
            )}
          >
            <Building2 className="h-4 w-4" />
            <span className="sr-only">Return to business list</span>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Return to business list</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
